import {
  GTMASOCardViewTracker,
  GTMSOCardClickTracker,
} from '@/app/[lng]/components/GTMTrackers/GTMSalesOfferTracker'
import PlanLogo from '@/app/[lng]/components/PlanLogo'
import OriginsDisplay from '@/app/[lng]/components/SalesOffer/OriginDisplayField'
import useConsentCheck from '@/app/hooks/useConsentCheck'
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver'
import { useTranslation } from '@/app/i18n/client'
import { ListType } from '@/app/types/constantDataType'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import { MutableRefObject, useEffect, useRef } from 'react'

const OfferCard = ({ cardData }: { cardData: any }) => {
  const { t } = useTranslation()
  const params = useParams<{ lng: string }>()

  const consentUserValue = useConsentCheck()

  const handleSOIntersection = () => {
    if (cardData.id) {
      GTMASOCardViewTracker({
        sales_offer_card_id: cardData.id,
        sales_offer_card_lang: params?.lng,
        sales_offer_card_company_id: cardData?.company?.id,
        userId: consentUserValue(),
      })
    }
  }
  const salesOfferCardRef: MutableRefObject<HTMLAnchorElement | null> =
    useRef(null)
  const observeElement = useIntersectionObserver(handleSOIntersection)
  useEffect(() => {
    if (salesOfferCardRef.current) {
      observeElement(salesOfferCardRef.current)
    }
  }, [observeElement])

  return (
    <Link
      ref={salesOfferCardRef}
      prefetch={false}
      onClick={() => {
        GTMSOCardClickTracker({
          sales_offer_card_clicked_id: cardData.id,
          sales_offer_card_lang: params?.lng,
          sales_offer_card_company_id: cardData?.company?.id,
          userId: consentUserValue(),
        })
      }}
      href={`${window.location.origin}/${params.lng ?? 'en'}/sales_offers/${cardData.id}`}
    >
      <div className="cursor-pointer relative  border pl-5 pt-5 overflow-hidden rounded-sm border-lightgreen_border bg-light_green hover:border-green_border">
        <div className="flex justify-between gap-7 mobile:gap-4">
          <div className="flex flex-col items-center gap-5 mobile:items-start">
            <div className="flex w-[140px] h-[140px] bg-[#ededed] rounded-sm overflow-hidden mobile:w-[100px] mobile:h-[100px]">
              <Image
                loading="lazy"
                width={140}
                height={140}
                src={
                  cardData?.images?.length > 0
                    ? cardData?.images[0]?.url
                    : '/assets/Icons/noImage/noImage2.svg'
                }
                alt=""
                className={
                  cardData?.images.length > 0
                    ? 'w-full h-full object-cover'
                    : 'w-full h-full object-contain'
                }
              />
            </div>
            {cardData?.company?.subscription &&
              cardData?.company?.subscription !== 'Free' && (
                <PlanLogo planType={cardData?.company?.subscription} />
              )}
          </div>
          <div className="flex w-full justify-between">
            <div className="relative gap-1 text-[#4e4e4e] text-xs font-normal">
              <p className="text-sm">
                {cardData?.created_at
                  ? cardData.created_at
                  : t('date not available')}
              </p>
              <h2 className="font-ns_bold text-lg flex gap-3  text-deep_black text-ellipsis pr-[3.7rem] mobile:text-[16px]">
                {cardData?.product?.name ?? ''}
                {cardData?.varieties?.length > 0
                  ? ` - ${cardData?.varieties[0]?.name}`
                  : ''}{' '}
                {cardData?.origins?.length > 0
                  ? ` - ${t('Origin')} ${cardData?.origins?.[0]?.name ?? ''}`
                  : ''}
              </h2>

              <div className="py-1">
                {cardData?.product_type?.length > 0 ? (
                  <span className="text-sm border bg-chip_bg border-chip_border rounded-full  px-2">
                    {t(
                      cardData?.product_type?.length === 2
                        ? cardData?.product_type?.join(' & ')
                        : cardData?.product_type?.[0]
                    )}
                  </span>
                ) : (
                  ''
                )}
              </div>
              <div className="flex flex-col gap-1 mt-2.5 font-ns_bold text-sm text-dark_text">
                <p className="font-ns_bold text-dark_text">
                  <OriginsDisplay origins={cardData?.origins} count={2} />
                </p>

                <p className="font-ns_bold text-dark_text">
                  {t('Caliber')}:
                  <span className="font-light ml-1">
                    {cardData?.size ? `${cardData?.size}` : t('NA')}
                  </span>
                </p>
                <span className="font-ns_bold flex">
                  {t('Quality')}:
                  <span className="font-light flex ml-1">
                    {cardData?.quality?.length > 0
                      ? cardData?.quality
                          ?.map((item: ListType) => item.name)
                          .join(', ')
                      : t('NA')}
                  </span>
                </span>
                <p className="font-ns_bold text-dark_text">
                  {t('Quantity')}:
                  <span className="font-light ml-1">
                    {cardData?.quantity > 0 ? cardData?.quantity : t('NA')}{' '}
                    {cardData?.quantity_unit
                      ? cardData?.quantity_unit?.name
                      : ''}
                  </span>
                </p>
                <p className="font-ns_bold text-dark_text">
                  {t('Packaging')}:
                  <span className="font-light ml-1">
                    {cardData?.packaging ? cardData?.packaging : t('NA')}
                  </span>
                </p>
                <p className="mb-5 font-ns_bold text-dark_text">
                  {t('Price')}:
                  <span className="font-light ml-1">
                    {cardData?.price_unit?.code}{' '}
                    {cardData?.price > 0 ? cardData?.price : t('NA')}
                    {cardData?.price_per?.name
                      ? `/ ${cardData?.price_per?.name}`
                      : t('')}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className=" flex flex-col absolute right-0 top-10">
            <div className=" top-0  bg-primary px-2 text-white p-1 uppercase font-ns_bold text-sm rounded-tl-[10%] rounded-bl-[10%]">
              {t('Offer')}
            </div>
          </div>
          {cardData?.is_sponsored && (
            <div className="absolute -right-8 bottom-6 bg-sponsor_color -rotate-45 text-xs flex  px-8">
              <Image
                width={17.4}
                height={17.4}
                src="/assets/Icons/blackStar.png"
                alt=""
              />
              {t('Sponsored')}
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

export default OfferCard
